import React, { useState, useEffect } from "react";
import "./summarypage.css";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../apiConfig";
import { buildDomainTagsFromGoal } from "./goalDomainTags";

const STATUS_OPTIONS = [
    "In progress ? on track",
    "In progress ? delayed",
    "Achieved",
    "Not achieved by target date",
];

const STATUS_DESCRIPTIONS = {
    "In progress ? on track":
        "Progress is occurring as expected and the goal remains realistic and feasible. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "In progress ? delayed":
        "Progress is occurring, but it is slower than expected or has extended beyond the original timeframe. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "Achieved":
        "The goal has been met and new goal setting is required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
    "Not achieved by target date":
        "Little or no progress has been made toward the goal by the target date. Re-evaluation and new goal setting are required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
};

// Legacy statuses saved in the DB under the old labels are mapped to the new
// wording so closed reviews display the updated option text.
const STATUS_MAP = {
    "In Progress- on track": "In progress ? on track",
    "In progress- on track": "In progress ? on track",
    "In Progress- delayed": "In progress ? delayed",
    "In progress- delayed": "In progress ? delayed",
    "Not Progressed as on date": "In progress ? delayed",
    "Not Progressed as on Date": "In progress ? delayed",
    "Achieved": "Achieved",
    "Not Achieved as on target date": "Not achieved by target date",
    "Not achieved as on target date": "Not achieved by target date",
};

const normalizeStatus = (s) => {
    const t = String(s || "").trim();
    return STATUS_MAP[t] || t;
};

const formatDateDisplay = (dateString) => {
    if (!dateString || dateString === "-" || dateString === "null" || dateString === "undefined") return "-";
    const raw = String(dateString).trim();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const match = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
        const y = match[1];
        const mIdx = parseInt(match[2], 10) - 1;
        const d = String(parseInt(match[3], 10)).padStart(2, "0");
        if (mIdx >= 0 && mIdx < 12) {
            return `${d} ${months[mIdx]} ${y}`;
        }
    }
    const match2 = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (match2) {
        const d = String(parseInt(match2[1], 10)).padStart(2, "0");
        const mIdx = parseInt(match2[2], 10) - 1;
        const y = match2[3];
        if (mIdx >= 0 && mIdx < 12) {
            return `${d} ${months[mIdx]} ${y}`;
        }
    }
    const date = new Date(raw);
    if (isNaN(date.getTime())) return dateString;
    const d = String(date.getDate()).padStart(2, "0");
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    return `${d} ${m} ${y}`;
};

// Some review goals store the skill merged into the domain as
// "Domain-Skill" (e.g. "Play & Cognition-Play and Cognition" when the
// domain has no sub-list). The "Domain - Skill" chip itself is produced by the
// shared builder in goalDomainTags.js, which applies that rule - the
// domain-only fallback is handled there, not here.

export default function PriviewsGoalReview({ sessionId }) {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [interventionsData, setInterventionsData] = useState(null);
    const [loadingInterventions, setLoadingInterventions] = useState(false);

    useEffect(() => {
        if (sessionId) {
            setCurrentSessionId(sessionId);
        }
    }, [sessionId]);

    const fetchInterventions = async () => {
        if (!currentSessionId) return;

        setInterventionsData(null);
        setLoadingInterventions(true);

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/get_test_intervention.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ session_id: parseInt(currentSessionId) }),
                }
            );

            const json = await response.json();

            if (json.status && json.data) {
                setInterventionsData(json);
            }
        } catch (error) {
            console.error("Error fetching interventions:", error.message);
        } finally {
            setLoadingInterventions(false);
        }
    };

    useEffect(() => {
        if (currentSessionId) fetchInterventions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId]);

    // -- Hide the entire preview until a review is actually closed ----------
    // A review only counts as closed when every one of its goals has
    // is_closed = 1 (set via the "Goal Closed" action, which requires status
    // "Achieved" first). Until such a review exists the whole preview UI must
    // stay hidden - data alone appearing is not enough.
    const gatedReviewGoals = interventionsData?.review_goals || [];
    const isGoalClosed = (g) => Number(g?.is_closed) === 1;
    // The editable form on the Goal Review page shows the LATEST review's
    // goals. Exclude that review from the preview so the closed form never
    // duplicates itself below. The preview only shows the PREVIOUS closed
    // review (the data created via a new Review pathway after the first
    // Goal Close).
    const latestReviewId =
        gatedReviewGoals.length > 0
            ? gatedReviewGoals[gatedReviewGoals.length - 1].review_id
            : null;
    const isOtherClosedReview = (rg) =>
        Array.isArray(rg.goals) &&
        rg.goals.length > 0 &&
        rg.goals.every(isGoalClosed) &&
        rg.review_id !== latestReviewId;
    const hasClosedReview = gatedReviewGoals.some(isOtherClosedReview);

    if (!hasClosedReview) {
        return null;
    }

    const getTodayDateString = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    if (loadingInterventions) {
        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft /> Previous page
                    </button>
                    <h2>Goal Review Preview</h2>
                </div>
                <p className="grv-loading" role="status" aria-live="polite">Loading...</p>
            </div>
        );
    }

    const sessionInfo = interventionsData?.session;
    const reviewGoals = interventionsData?.review_goals || [];

    const fallbackGoal = interventionsData?.goals?.[0] || {};

    // Only the most recently CLOSED review should appear beneath the Goal
    // Review form. A review counts as closed when every one of its goals has
    // is_closed = 1. In-progress updates (e.g. status "Achieved" without
    // closing) must not show up in this summary until the goal is officially
    // closed.
    const closedReviews = reviewGoals.filter(isOtherClosedReview);
    const latestClosedReview =
        closedReviews.length > 0
            ? closedReviews[closedReviews.length - 1]
            : null;

    const displayGoals = (latestClosedReview ? [latestClosedReview] : []).flatMap((rg) =>
        (rg.goals || []).map((g) => {
            const merged = { ...fallbackGoal, ...g };
            Object.keys(fallbackGoal).forEach((k) => {
                if (merged[k] == null || (Array.isArray(merged[k]) && merged[k].length === 0)) {
                    merged[k] = fallbackGoal[k];
                }
            });
            merged._review_no = rg.review_no;
            merged._review_created_at = rg.review_created_at;
            return merged;
        })
    );

    if (!sessionInfo) {
        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft /> Previous page
                    </button>
                    <h2>Goal Review Preview</h2>
                </div>
                <p className="grv-loading">No session data found.</p>
            </div>
        );
    }

    return (
        <div className="grv-page">

            <div className="grv-content">
                <h3 className="grv-client-name">
                    {sessionInfo.client_name || "Client"}
                </h3>

                <p className="grv-assessment-date">
                    Assessment Date- {(() => {
                        const first = displayGoals[0];
                        return formatDateDisplay(first?.created_at);
                    })()}
                </p>

                {displayGoals.length === 0 ? (
                    <p className="grv-loading">No closed goal reviews yet.</p>
                ) : (
                    displayGoals.map((goal, gIndex) => {
                        /* Same gate as goalreview.js: a row can exist with a
                           blank selection, which is still "no data". This is
                           the single condition for the whole section, so the
                           dosage/session counters and everything below are
                           skipped instead of rendering empty inputs. */
                        const goalInterventions = Array.isArray(goal.interventions)
                            ? goal.interventions.filter(Boolean)
                            : [];
                        const hasAnySelection = goalInterventions.some((iv) => {
                            const hasText = (v) =>
                                Array.isArray(v)
                                    ? v.some((s) => String(s).trim())
                                    : typeof v === "string" && v.trim() !== "";
                            return hasText(iv?.selected_intervention) || hasText(iv?.intervention_type);
                        });
                        const isLastGoal = gIndex === displayGoals.length - 1;

                        return (
                            <div className={`grv-goal-block${isLastGoal ? " grv-goal-block-last" : ""}`} key={`review-${goal.goal_id}`}>

                                <div className="grv-row-2col">
                                    <div className="grv-field">
                                        <label>Goal Domain </label>
                                        <div className="grv-box grv-readonly" style={{ padding: "7px 10px" }}>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                                {/* Paired domain + skill, so the chip reads
                                                    "Motor - Grasp And Release" like every other
                                                    goal view instead of the bare domain. */}
                                                {buildDomainTagsFromGoal(goal).map((tag, i) => (
                                                    <span key={i} style={{ background: "#e0e0e0", color: "#323C43", borderRadius: "4px", padding: "2px 10px", fontSize: "13px", fontWeight: "600", display: "inline-block" }}>{tag}</span>
                                                ))}
                                                {buildDomainTagsFromGoal(goal).length === 0 && <span style={{ color: "#555" }}>-</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grv-field">
                                        <label>Target Date</label>
                                        <div className="grv-box grv-readonly">{formatDateDisplay(goal.target_date)}</div>
                                    </div>
                                </div>

                                <div className="grv-field full">
                                    <label>Caregiver-led SMART goal</label>
                                    <div className="grv-box multiline grv-readonly">{goal.smart_goal || "-"}</div>
                                </div>

                                <p className="grv-subheading">
                                    Please update the current status of Caregiver-led SMART Goal{" "}
                                    {goal.goal_order || 1}
                                </p>

                                <div className="grv-row-2col">
                                    <div className="grv-field">
                                        <label>Goal Status</label>
                                        <div className="grv-box multiline grv-readonly">{normalizeStatus(goal.status) || STATUS_OPTIONS[0]}</div>
                                    </div>
                                    <div className="grv-field">
                                        <label>Result Date</label>
                                        <div className="grv-box grv-readonly">{formatDateDisplay(goal.result_date || getTodayDateString())}</div>
                                    </div>
                                </div>

                                <div className="grv-status-note grv-readonly">
                                    {STATUS_DESCRIPTIONS[normalizeStatus(goal.status)] || goal.status_content}
                                </div>

                                <label className="grv-label">
                                    What intervention/s has been selected by caregivers?
                                </label>

                                {!hasAnySelection ? (
                                    <div className="grv-box grv-readonly" style={{ marginTop: "10px" }}>
                                        No interventions selected for this goal.
                                    </div>
                                ) : (
                                    goalInterventions.map((iv, idx) => (
                                        <div className="grv-intervention-block" key={`${goal.goal_id}-${idx}`}>
                                            <div className="grv-intervention-row">
                                                {iv.intervention_type?.split(",").map((item, i2) => (
                                                    <span key={i2} className="grv-pill">{item.trim()}</span>
                                                ))}
                                            </div>

                                            <div className="grv-sub-title">1. Dosage Home Practice</div>
                                            <div className="grv-counters-3col">
                                                <div className="grv-counter-field">
                                                    <label>No. of minutes per day</label>
                                                    <div className="grv-counter">
                                                        <input className="grv-counter-input grv-readonly" value={iv.dosage_minutes_per_day ?? 0} readOnly aria-label="No. of minutes per day" />
                                                    </div>
                                                </div>
                                                <div className="grv-counter-field">
                                                    <label>No. of  Weeks</label>
                                                    <div className="grv-counter">
                                                        <input className="grv-counter-input grv-readonly" value={iv.dosage_weeks ?? 0} readOnly aria-label="No. of weeks" />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grv-sub-title">2. Therapist Sessions to Support</div>
                                            <div className="grv-counters-1col">
                                                <div className="grv-counter-field">
                                                    <label>No. of sessions</label>
                                                    <div className="grv-counter">
                                                        <input className="grv-counter-input grv-readonly" value={iv.therapist_sessions ?? 0} readOnly aria-label="No. of sessions" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}

                                {hasAnySelection && (
                                    <>
                                        <label className="grv-label">Outcome measure used</label>
                                        <div className="grv-box multiline grv-readonly">
                                            {goal.outcome_measure || "-"}
                                        </div>
                                        <div className="grv-outcome-box">
                                            <label>Outcome Measure</label>
                                            <div className="grv-box multiline grv-readonly">
                                                {goal.outcome_measure_used || "-"}
                                            </div>
                                        </div>
                                        <h3 className="grv-section-title">
                                            Review Key Components of Intervention Offered, Mode and Dose:
                                        </h3>
                                        <h4 className="grv-principles-title">Effective task-specific training incorporates five key principles: </h4>
                                        <p className="grv-principle-text"><strong className="grv-principle-label">- Specificity:</strong> Select meaningful, goal-directed tasks within the natural environment.  Tailor tasks specifically to the goal-limiting factors and stage of motor learning.  </p>
                                        <p className="grv-principle-text"><strong className="grv-principle-label"> - Repetition:</strong> Set-up activities and the environment to evoke high levels of active, self-initiated practice. Consider activities to support independent practice alongside caregiver-supported practice  </p>
                                        <p className="grv-principle-text"><strong className="grv-principle-label">- Practice:</strong> Use whole-task practice as the primary approach, with part-task practice used selectively to support skill progression. Distribute practice across the day and embed within everyday routines.  </p>
                                        <p className="grv-principle-text"><strong className="grv-principle-label">- Grading:</strong> Set up activities that are not so easy that learning is limited and not so difficult that the infant becomes frustrated. Task difficulty should be progressively adjusted as skills develop.</p>
                                        <p className="grv-principle-text"><strong className="grv-principle-label">- Feedback:</strong>Use intrinsic feedback through toys and the environment to support contingency-based learning. Use extrinsic feedback intentionally to support learning and motivation.</p>

                                        <p className="grv-reflect-instructions">
                                            Reflect on the intervention as a whole and all of the key components.
                                            You do not need to rate each component separately. Use the prompts
                                            below to identify strengths, areas for improvement, and next steps.
                                        </p>

                                        <div className="grv-field-inline">
                                            <label>What key components were demonstrated well?</label>
                                            <div className="grv-box grv-readonly">
                                                {goal.components_demonstrated || "-"}
                                            </div>
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>What key components could be strengthened further?</label>
                                            <div className="grv-box grv-readonly">
                                                {goal.components_strengthened || "-"}
                                            </div>
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>
                                                Note any specific actions you can change when delivering this
                                                intervention.
                                            </label>
                                            <div className="grv-box grv-readonly">
                                                {goal.components_specific_action || "-"}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })
                )}


                <style>{`
                .grv-page {
                    width: 100%;
                    max-width: 1600px;
                    margin: 0 auto;
                    background: #eaf6ea;
                    min-height: 100vh;
                    padding-bottom: 40px;
                    box-sizing: border-box;
                }
                .grv-page.getall {
                    margin-top: 24px;
                }
                .grv-readonly {
                    background: #eceeef !important;
                    color: #666 !important;
                    cursor: not-allowed;
                    user-select: text;
                }
                .grv-header-bar {
                    position: relative;
                    background: #247A33;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 14px 16px;
                    border-bottom: 3px solid #f7f8fa;
                    min-height: 50px;
                }
                .grv-header-bar h2 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 700;
                }
                .grv-back-btn {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: white;
                    border: 1px solid rgba(255,255,255,0.6);
                    color: #247A33;
                    border-radius: 5px;
                    padding: 6px 12px;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    font-weight: 700;
                }
                .grv-content {
                    padding: 18px 70px 10px;
                }
                .grv-loading {
                    padding: 24px 20px;
                    text-align: center;
                }
                .grv-client-name {
                    text-align: center;
                    color: #247A33;
                    font-size: 22px;
                    margin: 4px 0 2px;
                }
                .grv-assessment-date {
                    text-align: left;
                    color: #247A33;
                    font-weight: 600;
                    margin: 0 0 18px;
                    font-size:24px;
                }
                .grv-goal-block {
                    border-bottom: 2px solid #b6d7b6;
                    padding-bottom: 26px;
                    margin-bottom: 26px;
                }
                .grv-goal-block:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-goal-block.grv-goal-block-last {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-row-2col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 20px;
                }
                .grv-field {
                    display: flex;
                    flex-direction: column;
                }
                .grv-field.full {
                    margin-bottom: 20px;
                }
                .grv-field label,
                .grv-label {
                    font-size: 16px;
                    color: #247A33;
                    font-weight: 700;
                    margin-bottom: 6px;
                    display: block;
                }
                .grv-box {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #f0f0f0;
                    color: #555;
                }
                .grv-box.multiline {
                    min-height: 44px;
                }
                .grv-date-input {
                    width: 100%;
                    box-sizing: border-box;
                    font-family: inherit;
                }
                .grv-select {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #fff;
                    color: #333;
                }
                .grv-subheading {
                    font-weight: 700;
                    font-style: italic;
                    color: #263238;
                    margin: 4px 0 12px;
                    font-size: 14px;
                }
                .grv-status-note {
                    background: #fff;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 16px;
                    color: #333;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
                .intervention-tags1 {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin: 10px 0 18px;
                }
                .intervention-tag1 {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 16px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-intervention-block {
                    padding-top: 16px;
                    margin-top: 16px;
                }
                .grv-intervention-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 20px;
                    font-weight: 700;
                    color: #247A33;
                }
                .grv-pill {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 5px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-sub-title {
                    font-weight: 700;
                    color: #247A33;
                    margin: 14px 0 10px;
                    font-size: 14px;
                }
                .grv-counters-3col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                    margin-bottom: 4px;
                }
                .grv-counters-1col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                }
                .grv-counter-field {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    max-width: 320px;
                    margin-bottom: 20px;
                }
                .grv-counter-field label {
                    font-size: 13px;
                    color: #247A33;
                    font-weight: 700;
                    display: inline-block;
                }
                .grv-counter {
                    display: inline-flex;
                    align-items: stretch;
                    gap: 6px;
                    min-width: 64px;
                    flex-shrink: 0;
                }
                .grv-counter-input {
                    border: 1px solid #90caf9;
                    border-radius: 6px;
                    padding: 6px 10px;
                    background: #fff;
                    font-weight: 600;
                    color: #333;
                    width: 60px;
                    font-size: 14px;
                    font-family: inherit;
                    -moz-appearance: textfield;
                }
                .grv-counter-input::-webkit-outer-spin-button,
                .grv-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .grv-counter-arrows {
                    display: flex;
                    flex-direction: column;
                }
                .grv-counter-arrows button {
                    background: #1976d2;
                    color: #fff;
                    border: none;
                    font-size: 11px;
                    line-height: 1;
                    width: 20px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    border-radius: 4px;
                    margin-bottom: 3px;
                    font-weight: 700;
                }
                .grv-counter-arrows button:last-child { margin-bottom: 0; }
                .grv-section-title {
                    color: #247A33;
                    font-size: 24px;
                    padding: 22px 0 8px;
                }
                .grv-principles-title {
                    color: #247A33;
                    font-size: 20px;
                    padding: 0px 0 0px;
                }
                .grv-principle-text {
                    font-size: 16px;
                    line-height: 1.6;
                    color: #333;
                    margin: 0 0 10px;
                }
                .grv-principle-label {
                    font-size: 18px;
                    color: #247A33;
                    text-align: justify;
                }
                .grv-reflect-instructions {
                    text-align: center;
                    font-weight: 600;
                    font-size: 18px;
                    color: #263238;
                    padding: 30px 0px 18px 0px;
                }
                .grv-iv-tiles {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    margin: 16px 0 6px;
                }
                .grv-iv-tile {
                    flex: 0 0 200px;
                    width: 200px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #fff;
                    transition: box-shadow 0.2s;
                    cursor: pointer;
                    text-align: center;
                }
                .grv-iv-tile:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .grv-iv-tile-open {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    border-color: #247A33;
                }
                .grv-iv-tile-image {
                    width: 100%;
                    height: 140px;
                    overflow: hidden;
                    background: #e8f5e9;
                }
                .grv-iv-tile-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }
                .grv-iv-tile-label {
                    padding: 12px 14px;
                    color: #247A33;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 1.4;
                    word-break: break-word;
                }
                .grv-iv-tile-details {
                    flex: 0 0 100%;
                    width: 100%;
                    display: flex;
                    gap: 20px;
                    background: #fff;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 16px;
                    box-sizing: border-box;
                }
                .grv-iv-tile-details-image {
                    flex: 0 0 180px;
                }
                .grv-iv-tile-details-image img {
                    width: 100%;
                    border-radius: 8px;
                    border: 2px solid #2f7a3d;
                    display: block;
                }
                .grv-iv-tile-details-text {
                    flex: 1;
                    min-width: 0;
                }
                .grv-iv-tile-details-text h5 {
                    color: #247A33;
                    font-size: 16px;
                    font-weight: 700;
                    margin: 0 0 10px;
                }
                .grv-iv-tile-details-text p {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #333;
                    margin: 0;
                    white-space: pre-line;
                    text-align: justify;
                }
                .grv-field textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 60px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                .grv-field-inline {
                    display: flex;
                    align-items: flex-start;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .grv-field-inline label {
                    flex: 0 0 260px;
                    max-width: 260px;
                    font-size: 15px;
                    color: #247A33;
                    font-weight: 700;
                    line-height: 1.4;
                }
                .grv-field-inline textarea,
                .grv-field-inline .grv-box {
                    flex: 1;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 14px;
                    min-height: 72px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                @media (max-width: 700px) {
                    .grv-field-inline {
                        flex-direction: column;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-iv-tile {
                        flex: 1 1 150px;
                        width: auto;
                        max-width: 200px;
                    }
                    .grv-iv-tile-details {
                        flex-direction: column;
                    }
                    .grv-iv-tile-details-image {
                        flex: 0 0 auto;
                        max-width: 220px;
                    }
                }
                .grv-outcome-box {
                    border: 1px solid #90a4ae;
                    border-radius: 8px;
                    padding: 14px;
                    margin-top: 20px;
                    background: #fff;
                }
                .grv-outcome-box label {
                    font-weight: 700;
                    color: #247A33;
                    font-size: 13px;
                    display: block;
                    margin-bottom: 8px;
                }
                .grv-outcome-box textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 50px;
                    resize: vertical;
                    font-family: inherit;
                }
                .grv-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 26px;
                }
                .grv-update-btn {
                    background: #247A33;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 22px;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }
                .grv-close-btn {
                    background: #fff;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 8px;
                    padding: 10px 18px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .grv-spin {
                    animation: grv-spin 0.9s linear infinite;
                }
                .grv-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
}
.grv-modal-card {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 360px;
    padding: 28px 24px 22px;
    text-align: center;
    position: relative;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}
.grv-modal-close-x {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #323C43;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.grv-modal-icon {
    font-size: 40px;
    color: var(--cpa-yellow-ink, #8A6D00);
    margin-bottom: 6px;
}
.grv-modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #222;
    margin: 6px 0 10px;
}
.grv-modal-text {
    font-size: 14px;
    color: #555;
    line-height: 1.5;
    margin: 0 0 22px;
}
.grv-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}
.grv-modal-yes {
    background: #247A33;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}
.grv-modal-no {
    background: #d32f2f;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}    
                @keyframes grv-spin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 1024px) {
                    .grv-content {
                        padding: 16px 32px 10px;
                    }
                }

                @media (max-width: 768px) {
                    .grv-content {
                        padding: 14px 20px 10px;
                    }
                    .grv-header-bar h2 {
                        font-size: 18px;
                    }
                    .grv-client-name {
                        font-size: 19px;
                    }
                    .grv-assessment-date {
                        font-size: 18px;
                    }
                    .grv-row-2col {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    .grv-section-title {
                        font-size: 20px;
                    }
                    .grv-principles-title {
                        font-size: 17px;
                    }
                    .grv-principle-text {
                        font-size: 14px;
                    }
                    .grv-principle-label {
                        font-size: 15px;
                    }
                    .grv-field-inline {
                        flex-direction: column;
                        gap: 8px;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-footer {
                        flex-wrap: wrap;
                    }
                }

                @media (max-width: 480px) {
                    .grv-header-bar {
                        padding: 10px 12px;
                        min-height: 44px;
                    }
                    .grv-header-bar h2 {
                        font-size: 16px;
                    }
                    .grv-back-btn {
                        left: 8px;
                        padding: 5px 8px;
                        font-size: 12px;
                    }
                    .grv-content {
                        padding: 12px 12px 8px;
                    }
                    .grv-client-name {
                        font-size: 17px;
                    }
                    .grv-assessment-date {
                        font-size: 15px;
                        margin-bottom: 12px;
                    }
                    .grv-goal-block {
                        padding-bottom: 18px;
                        margin-bottom: 18px;
                    }
                    .grv-field label,
                    .grv-label {
                        font-size: 14px;
                    }
                    .grv-box,
                    .grv-select {
                        font-size: 13px;
                        padding: 8px 10px;
                    }
                    .grv-subheading {
                        font-size: 13px;
                    }
                    .grv-status-note {
                        font-size: 12px;
                        padding: 10px 12px;
                    }
                    .grv-sub-title {
                        font-size: 13px;
                    }
                    .grv-pill {
                        font-size: 12px;
                        padding: 5px 10px;
                    }
                    .grv-intervention-row {
                        flex-wrap: wrap;
                    }
                    .grv-counter-field {
                        max-width: 100%;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    }
                    .grv-counter-field label {
                        font-size: 12px;
                        flex: 1 1 auto;
                        min-width: 0;
                    }
                    .grv-counter {
                        flex: 0 0 auto;
                    }
                    .grv-section-title {
                        font-size: 18px;
                        margin: 18px 0 6px;
                    }
                    .grv-principles-title {
                        font-size: 15px;
                    }
                    .grv-principle-text {
                        font-size: 13px;
                    }
                    .grv-principle-label {
                        font-size: 14px;
                    }
                    .grv-reflect-instructions {
                        font-size: 13px;
                    }
                    .grv-field-inline label {
                        font-size: 14px;
                    }
                    .grv-field-inline textarea,
                    .grv-field textarea,
                    .grv-outcome-box textarea {
                        font-size: 13px;
                        padding: 10px 12px;
                    }
                    .grv-outcome-box {
                        padding: 12px;
                    }
                    .grv-footer {
                        flex-direction: column-reverse;
                        gap: 10px;
                    }
                    .grv-footer button {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 360px) {
                    .grv-header-bar h2 {
                        font-size: 14px;
                    }
                    .grv-back-btn {
                        font-size: 11px;
                        padding: 4px 7px;
                    }
                    .grv-content {
                        padding: 10px 10px 6px;
                    }
                    .grv-counter-input {
                        width: 50px;
                    }
                }
            `}</style>
            </div>
        </div>
    );
}

